import React from 'react';
import { Link } from 'react-router-dom';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { ITEMS_THEME, btnSecondarySm, itemTypeBadgeClass } from './itemsTheme';
import { panelHeadingCls, statLabelCls } from './ItemsHeadings';
import ItemCard from './ItemCard';
import { SortableHeader } from '../../utils/tableSort';
import PaginationControls from '../../utils/PaginationControls';
import GlobalExportButtons from '../GlobalExportButtons';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';
import {
  Filter,
  Package,
  Pencil,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';

export default function ItemListHistory({
  embedded = false,
  companyCurrency,
  companyDetails,
  getExportData,
  getExportColumns,
  getReportDetails,
  itemsList,
  filteredItems,
  pagination,
  sortConfig,
  handleSort,
  searchTerm,
  filterType,
  filterGstRate,
  filterActive,
  onSearchChange,
  onFilterTypeChange,
  onFilterGstRateChange,
  onFilterActiveChange,
  onClearFilters,
  getLiveStock,
  selectedForDeletion,
  onToggleSelect,
  onSelectAllPage,
  onEdit,
  onDelete,
  onBulkDeleteClick,
  isBulkDeleting,
  showMobileFilters,
  setShowMobileFilters,
  expandedCards,
  toggleCard,
  splitLayout = false,
  matchedCount,
  totalCount,
}) {
  const theme = ITEMS_THEME;
  const listCount = filteredItems.length;
  const hasFilters =
    searchTerm || filterType !== 'All' || filterGstRate !== 'All' || filterActive !== 'All';
  const rows = pagination.currentData || [];
  const sym = getCurrencySymbol(companyCurrency);

  const filterInputClass = `w-full rounded-md border border-slate-200 bg-white pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${theme.filterFocus}`;
  const filterSelectClass = `rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${theme.filterFocus}`;

  const displayMatched = matchedCount ?? listCount;
  const displayTotal = totalCount ?? itemsList.length;

  const toolbar = (
    <div className={theme.listPanelHeader}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className={embedded ? 'text-sm font-bold tracking-tight text-slate-900' : panelHeadingCls}>
            {theme.historyTitle}
          </h3>
          <p className={`text-xs mt-0.5 ${theme.subtitleOnHeader}`}>
            <span className="tabular-nums font-semibold text-amber-800">{displayMatched}</span> shown
            {displayMatched !== displayTotal && (
              <>
                {' '}
                · <span className="tabular-nums">{displayTotal}</span> total
              </>
            )}
            {splitLayout && embedded && (
              <span className="text-slate-500">
                {' '}
                · <span className="tabular-nums">{rows.length}</span> on page
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0 w-full sm:w-auto">
          {selectedForDeletion.size > 0 && (
            <button
              type="button"
              onClick={onBulkDeleteClick}
              disabled={isBulkDeleting}
              className="inline-flex items-center gap-1 min-h-[44px] px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 touch-manipulation"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete ({selectedForDeletion.size})
            </button>
          )}
          <GlobalExportButtons
            data={getExportData}
            columns={getExportColumns}
            filename="ITEMS_LIST"
            title="Items List"
            companyDetails={companyDetails}
            reportDetails={getReportDetails}
            disabled={itemsList.length === 0}
          />
          <Link to="/import-data" className={theme.importLink}>
            <Upload className="h-3.5 w-3.5" aria-hidden />
            Import
          </Link>
        </div>
      </div>
    </div>
  );

  const sortTh = theme.sortTh;
  const sortActive = theme.sortActive;

  const filtersBar = (
    <div className={`hidden lg:block px-4 py-2.5 border-b border-slate-100 ${theme.filtersAreaBg}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={theme.searchPlaceholder}
            className={filterInputClass}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AppSelect
            value={filterType}
            onChange={(e) => onFilterTypeChange(e.target.value)}
            aria-label="Filter by type"
            sheetTitle="Item type"
            options={[
              { value: 'All', label: 'All types' },
              { value: 'Goods', label: 'Goods' },
              { value: 'Services', label: 'Services' },
              { value: 'Restaurant', label: 'Restaurant' },
              { value: 'Other', label: 'Other' },
            ]}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} ${filterSelectClass} min-w-[7rem]`}
          />
          <AppSelect
            value={filterGstRate}
            onChange={(e) => onFilterGstRateChange(e.target.value)}
            aria-label="Filter by GST"
            sheetTitle="GST rate"
            options={[
              { value: 'All', label: 'All GST' },
              { value: '0', label: '0%' },
              { value: '5', label: '5%' },
              { value: '12', label: '12%' },
              { value: '18', label: '18%' },
              { value: '28', label: '28%' },
            ]}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} ${filterSelectClass} min-w-[7rem]`}
          />
          <AppSelect
            value={filterActive}
            onChange={(e) => onFilterActiveChange(e.target.value)}
            aria-label="Filter by status"
            sheetTitle="Status"
            options={[
              { value: 'All', label: 'All status' },
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} ${filterSelectClass} min-w-[7rem]`}
          />
          {hasFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-xs font-medium text-amber-800 hover:text-amber-950 px-2"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const shellClass = embedded
    ? 'flex flex-col flex-1 min-h-0 h-full max-h-full w-full overflow-hidden'
    : 'rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden ring-1 ring-black/5 h-full flex flex-col w-full';

  const listScrollArea = embedded
    ? 'flex-1 min-h-0 overflow-y-auto overscroll-y-contain'
    : '';
  const desktopListShell = embedded
    ? 'hidden lg:flex lg:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden'
    : 'hidden lg:block';
  const mobileListShell = embedded
    ? 'lg:hidden flex flex-col min-h-[50dvh] overflow-visible'
    : 'lg:hidden';

  return (
    <div className={shellClass}>
      <div className="shrink-0">
        {toolbar}
        <div className={theme.headerAccentLine} aria-hidden />
      </div>

      {!embedded && !splitLayout && (
        <div className="grid gap-2 p-2 grid-cols-2 lg:grid-cols-3 border-b border-slate-100">
          <div className={theme.statCard}>
            <span className={statLabelCls}>This page</span>
            <p className="text-base font-bold text-gray-900 tabular-nums">{rows.length}</p>
          </div>
          <div className={theme.statCard}>
            <span className={statLabelCls}>Matched</span>
            <p className={`text-base font-bold tabular-nums ${theme.amount}`}>{displayMatched}</p>
          </div>
          <div className={`${theme.statCard} col-span-2 lg:col-span-1 flex flex-col justify-center gap-2`}>
            <GlobalExportButtons
              data={getExportData}
              columns={getExportColumns}
              filename="ITEMS_LIST"
              title="Items List"
              companyDetails={companyDetails}
              reportDetails={getReportDetails}
              disabled={itemsList.length === 0}
            />
            <Link to="/import-data" className={theme.importLinkLg}>
              <Upload className="h-4 w-4" aria-hidden />
              Import items
            </Link>
          </div>
        </div>
      )}

      <div className="shrink-0">{filtersBar}</div>

      <div className={desktopListShell}>
        <div className={`${listScrollArea} overflow-x-auto`.trim()}>
          {listCount === 0 ? (
            <div className="py-10 text-center">
              <div className={`mx-auto w-14 h-14 rounded-full ${theme.emptyBg} flex items-center justify-center mb-3`}>
                <Package className={`h-7 w-7 ${theme.emptyIcon}`} />
              </div>
              <p className="text-gray-500 font-medium">No items found</p>
              <p className="text-sm text-gray-400 mt-1">
                {hasFilters ? 'Try adjusting filters' : 'Use Add new item to create your first entry'}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <colgroup>
                <col className="w-10" />
                <col className="min-w-[12rem]" />
                <col className="w-[7%]" />
                <col className="w-[10%]" />
                <col className="w-[11%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[6.5rem]" />
              </colgroup>
              <thead className={theme.tableHeadSticky}>
                <tr
                  className={`${theme.tableHead} text-[11px] font-semibold uppercase tracking-wider`}
                >
                  <th className="px-3 py-2.5 text-center align-middle w-11">
                    <input
                      type="checkbox"
                      checked={selectedForDeletion.size === rows.length && rows.length > 0}
                      onChange={onSelectAllPage}
                      className={`rounded border-gray-300 ${theme.checkbox}`}
                      aria-label="Select all on page"
                    />
                  </th>
                  <SortableHeader
                    columnKey="itemName"
                    label="Item"
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    align="left"
                    className={sortTh}
                    activeHeaderClassName={sortActive}
                  />
                  <SortableHeader
                    columnKey="quantityMeasurement"
                    label="Unit"
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    align="left"
                    className={sortTh}
                    activeHeaderClassName={sortActive}
                  />
                  <SortableHeader
                    columnKey="defaultRate"
                    label={`Rate (${sym})`}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    align="right"
                    className={sortTh}
                    activeHeaderClassName={sortActive}
                  />
                  <SortableHeader
                    columnKey="itemType"
                    label="Type"
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    align="center"
                    className={sortTh}
                    activeHeaderClassName={sortActive}
                  />
                  <SortableHeader
                    columnKey="gstPercentage"
                    label="GST"
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    align="center"
                    className={sortTh}
                    activeHeaderClassName={sortActive}
                  />
                  <SortableHeader
                    columnKey="stock"
                    label="Stock"
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    align="right"
                    className={sortTh}
                    activeHeaderClassName={sortActive}
                  />
                  <th className="px-3 py-2.5 text-center align-middle w-[5.5rem]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item, index) => {
                  const rowNum = (pagination.startIndex || 0) + index + 1;
                  const zebra = rowNum % 2 === 0;
                  const liveStock = getLiveStock(item);
                  return (
                    <tr
                      key={item.id || `row-${index}`}
                      className={`border-b border-slate-100 transition-colors ${theme.hoverRow} ${
                        zebra ? 'bg-white' : theme.zebra
                      }`}
                    >
                      <td className="px-3 py-2.5 text-center align-middle">
                        <input
                          type="checkbox"
                          checked={selectedForDeletion.has(item.id)}
                          onChange={() => onToggleSelect(item.id)}
                          className={`rounded border-gray-300 ${theme.checkbox}`}
                        />
                      </td>
                      <td
                        className="px-3 py-2.5 text-left align-middle font-medium text-gray-900 truncate"
                        title={item.itemName}
                      >
                        {item.itemName}
                      </td>
                      <td className="px-3 py-2.5 text-left align-middle text-gray-700 truncate">
                        {item.quantityMeasurement || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right align-middle tabular-nums">
                        <span
                          className={`inline-block rounded-md px-2 py-0.5 text-xs ${theme.amountBg}`}
                        >
                          {sym}
                          {item.defaultRate ?? 0}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center align-middle">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset ${itemTypeBadgeClass(item.itemType)}`}
                        >
                          {item.itemType}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center align-middle tabular-nums text-gray-700">
                        {typeof item.gstPercentage !== 'undefined' ? `${item.gstPercentage}%` : '0%'}
                      </td>
                      <td className="px-3 py-2.5 text-right align-middle font-semibold tabular-nums text-gray-900">
                        {liveStock}
                      </td>
                      <td className="px-3 py-2.5 text-center align-middle">
                        <div className="inline-flex items-center justify-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => onEdit(item)}
                            className={`${btnSecondarySm} min-h-[40px] min-w-[40px] p-2`}
                            title="Edit"
                            aria-label={`Edit ${item.itemName}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(item.id)}
                            className="p-2 min-h-[40px] min-w-[40px] inline-flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
                            title="Delete"
                            aria-label={`Delete ${item.itemName}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {listCount > 0 && (
          <div className="shrink-0 px-2 py-2 border-t border-slate-100 bg-white">
            <PaginationControls {...pagination} />
          </div>
        )}
      </div>

      <div className={mobileListShell}>
        <div className="shrink-0 p-2.5 pb-0 space-y-2">
          <div className="relative lg:hidden">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={theme.searchPlaceholder}
              className={`${filterInputClass} min-h-[44px]`}
            />
          </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            aria-expanded={showMobileFilters}
            className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] rounded-lg border transition-all w-full justify-center ${
              hasFilters ? theme.mobileFilterActive : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Filter className={`h-4 w-4 ${hasFilters ? theme.filterIcon : ''}`} />
            <span className={`text-sm font-bold ${hasFilters ? theme.filterActiveText : 'text-gray-700'}`}>Filters</span>
            {hasFilters && (
              <span className={`${theme.mobileFilterBadge} text-white text-xs px-1.5 py-0.5 rounded-full`}>
                {[searchTerm, filterType !== 'All', filterGstRate !== 'All', filterActive !== 'All'].filter(Boolean).length}
              </span>
            )}
          </button>
          {showMobileFilters && (
            <div className="mt-2 bg-white border border-amber-100 rounded-xl shadow-lg p-4 space-y-3 z-20 relative">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={theme.searchPlaceholder}
                  className={filterInputClass}
                />
              </div>
              <AppSelect
                value={filterType}
                onChange={(e) => onFilterTypeChange(e.target.value)}
                aria-label="Filter by type"
                sheetTitle="Item type"
                options={[
                  { value: 'All', label: 'All types' },
                  { value: 'Goods', label: 'Goods' },
                  { value: 'Services', label: 'Services' },
                  { value: 'Restaurant', label: 'Restaurant' },
                  { value: 'Other', label: 'Other' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} ${filterSelectClass} w-full`}
              />
              <AppSelect
                value={filterGstRate}
                onChange={(e) => onFilterGstRateChange(e.target.value)}
                aria-label="Filter by GST"
                sheetTitle="GST rate"
                options={[
                  { value: 'All', label: 'All GST rates' },
                  { value: '0', label: '0%' },
                  { value: '5', label: '5%' },
                  { value: '12', label: '12%' },
                  { value: '18', label: '18%' },
                  { value: '28', label: '28%' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} ${filterSelectClass} w-full`}
              />
              <AppSelect
                value={filterActive}
                onChange={(e) => onFilterActiveChange(e.target.value)}
                aria-label="Filter by status"
                sheetTitle="Status"
                options={[
                  { value: 'All', label: 'All status' },
                  { value: 'Active', label: 'Active only' },
                  { value: 'Inactive', label: 'Inactive only' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} ${filterSelectClass} w-full`}
              />
              {hasFilters && (
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="w-full min-h-[44px] py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
        </div>

        <div className={`${listScrollArea} p-2.5 space-y-2.5`}>
          {listCount === 0 ? (
            <div className="py-12 text-center text-gray-500 text-sm">No items match your filters.</div>
          ) : (
            rows.map((item, index) => (
              <ItemCard
                key={item.id || `m-${index}`}
                item={item}
                liveStock={getLiveStock(item)}
                companyCurrency={companyCurrency}
                isSelected={selectedForDeletion.has(item.id)}
                onToggleSelect={onToggleSelect}
                isExpanded={!!expandedCards[item.id]}
                onToggle={toggleCard}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          )}
        </div>
        {listCount > 0 && (
          <div className="shrink-0 px-2.5 py-2 border-t border-slate-100 bg-white">
            <PaginationControls {...pagination} />
          </div>
        )}
      </div>
    </div>
  );
}
